import styles from "./PowerfulMessage.module.scss"

import React, { memo, useEffect, useRef } from "react"
import { useParams } from "react-router-dom"

import { GROUP } from "@shared/config"
import { useAccount, useSchedule } from "@shared/contexts"
import { Message } from "@shared/hooks/useMessages"

import classNames from "classnames"
import dayjs from "dayjs"

import BlindButton from "../BlindButton"
import MessageBody from "../MessageBody"
import Nickname from "../Nickname"
import ViewerControlMenu from "./ViewerControlMenu";

interface Props {
  scrollMessages?: Function
  message: Message
  isNicknameStyled?: boolean
  showBlindButton?: boolean
  scheduleId?: number
  onImageLoad?: () => void
}

export default memo(function PowerfulMessage({
  scrollMessages,
  message,
  isNicknameStyled = false,
  showBlindButton,
  scheduleId,
  onImageLoad,
}: Props) {
  const messageRef = useRef<HTMLParagraphElement>(null)
  const { matchId } = useParams<{ matchId?: string }>()
  const { schedule } = useSchedule()
  const { account } = useAccount()

  const isStreamerMessage = GROUP.STREAMER.includes(message.role)
  const streamerNickName = schedule?.user.nickname
  const username = account.viewer?.username
  const isSystemMessage = message.role === "SYSTEM"
  const isStreamer = GROUP.STREAMER.includes(account.viewer?.role as string)
  const isAnonymous = account.viewer?.role === "ANONYMOUS"

  useEffect(() => {
    if (!matchId || !messageRef.current) return

    if (message.key === matchId) {
      const { offsetTop } = messageRef.current
      messageRef.current.focus()

      scrollMessages && scrollMessages(offsetTop)
    }
  }, [message, messageRef, matchId, scrollMessages])

  useEffect(() => {
    if (!messageRef || !messageRef.current) return
  }, [messageRef])

  const messageSizeStyle = message.size && {
    [styles[`${message.size}M`]]: true,
  }

  if (!showBlindButton && message.isBlind) {
    return <p className={styles.gray}>삭제된 메시지 입니다.</p>
  }

  if (message.isSystem && !message.emoji) {
    const targetUsername = message.userKey.split("-").slice(0, -1).join("-")

    return ["system", username].includes(targetUsername) ? (
      <p
        className={message.isWelcome ? styles.welcome : styles.system}>
        { message.nickname ? `[${message.nickname}] ${message.body}` : message.body }
      </p>
    ) : null
  }

  return (
    <p
      ref={messageRef}
      className={classNames(
        styles.message,
        {
          [styles.pick]: message.key === matchId,
          [styles.bold]: message.isBold,
          [styles.blind]: showBlindButton && message.isBlind,
        },
        messageSizeStyle,
      )}
      style={message.color ? { color: `${message.color}` } : { color: "#000" }}
    >
      {showBlindButton && !isStreamerMessage && (
        <BlindButton
          messageKey={message.key}
          scheduleId={scheduleId}
          isBlind={message.isBlind}
        />
      )}
      { (message.isWelcome && isAnonymous) ? null : <em>[{dayjs(message.timestamp).format("HH:mm")}]</em> }
      {isStreamer ? (
        <ViewerControlMenu message={message} >
          <Nickname
            role={message.role}
            emoji={message.emoji}
            nickname={message.isSystem ? streamerNickName : message.nickname}
            isNicknameStyled={isNicknameStyled}
          />
        </ViewerControlMenu>
      ) : (
        (message.isWelcome && isAnonymous) ? null :
          <Nickname
            role={message.role}
            emoji={message.emoji}
            nickname={message.isSystem ? streamerNickName : message.nickname}
            isNicknameStyled={isNicknameStyled}
          />
      )}

      <MessageBody
        messagesRef={messageRef}
        isStreamerMessage={isStreamerMessage}
        message={message}
        onImageLoad={onImageLoad}
      />
    </p>
  )
})
